<script lang="ts">
  import { book, step } from "../../stores/WalkinBookingStore";
  import { convert24HoursFormat, groupTimesByHour } from "../../helpers";

  export let timeslots: string[] = [];
  export let selectedTimeslot: string = "";
  let localData: any;
  let isOpen = false;
  let previousAdmission: string | null = null;

  let timeslotsChildrens: string[] = [];
  let showTimeslotsChildrens: boolean = false;

  // Subscribe to store changes and update local data
  book.subscribe((obj) => {
    if (obj) {
      localData = obj;
      // Update timeslots when they change in the store
      if (obj.timeslots) {
        timeslots = obj.timeslots;
      }
      // Update selectedTimeslot from store
      if (obj.timeslot !== undefined) {
        selectedTimeslot = obj.timeslot;
      }
      // Only handle admission changes - timeslots will be updated by WalkinBooking component
      if (obj.admission !== previousAdmission) {
        previousAdmission = obj.admission;
      }
    }
  });

  const toggleDropdown = () => {
    isOpen = !isOpen;
  };

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest(".custom-select")) {
      isOpen = false;
    }
  };

  $: selectedTimeslotName = selectedTimeslot || "Select Time Slot";

  const timeslotChange = (e: any) => {
    const newTimeslot = e.target.value;
    if (!newTimeslot) return;

    selectedTimeslot = newTimeslot;
    isOpen = false;

    // Update store with new timeslot
    book.update((value) => ({ ...value, timeslot: newTimeslot }));
  };

  const timeslotChangeParent = (e: any) => {
    showTimeslotsChildrens = false; // change if we want to show the in-between minutes childens buttons
    timeslotsChildrens = timeslotsGrouped[e.target.value];
    isOpen = false;

    // Update store with new timeslot
    book.update((value) => ({ ...value, timeslot: e.target.value }));
  };

  function onNextStep() {
    if (!selectedTimeslot) {
      return;
    }
    step.update((value) => value + 1);
  }

  $: timeslotsGrouped = groupTimesByHour(timeslots);
</script>

<svelte:window on:click={handleClickOutside} />

<div class="max-w-3xl w-full mx-auto relative mb-2">
  <!-- Header -->
  <div class="flex flex-col items-center justify-center mb-2">
    <h3
      class="text-lg font-semibold uppercase font-helveticaneue-bold-extended"
    >
      Select Time Slot
    </h3>
    <p class="text-base text-gray-600">
      Select a time slot to book your walk-in
    </p>
  </div>
  <!-- ./Header -->
  <div>
    <div class="custom-select relative w-full">
      <div
        class="selected-value flex items-center justify-between px-4 py-4 border border-gray-300 rounded-lg cursor-pointer bg-white relative pl-6"
        on:click={toggleDropdown}
      >
        <span class="selected-text text-gray-700 w-full text-center uppercase">
          {#if selectedTimeslotName === "Select Time Slot"}
            Select Time Slot
          {:else}
            {convert24HoursFormat(selectedTimeslotName)}
          {/if}
        </span>
        <svg
          class="w-4 h-4 text-gray-500 transform transition-transform {isOpen
            ? 'rotate-180'
            : ''}"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
      <ul
        class="options-list absolute w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto z-10 w-full text-center uppercase {isOpen
          ? ''
          : 'hidden'}"
      >
        {#if timeslotsGrouped && Object.keys(timeslotsGrouped).length > 0}
          {#each Object.keys(timeslotsGrouped) as timeslot}
            <li
              class="px-4 py-4 hover:bg-gray-100 cursor-pointer"
              data-value={timeslot}
              on:click={() => {
                timeslotChangeParent({ target: { value: timeslot } });
              }}
            >
              {convert24HoursFormat(timeslot)}
            </li>
          {/each}
        {:else}
          <li class="px-4 py-4 hover:bg-gray-100 cursor-pointer">
            No timeslots available
          </li>
        {/if}
      </ul>
    </div>
    <select
      bind:value={selectedTimeslot}
      class="w-full text-center rounded-lg hidden"
      on:change={timeslotChange}
    >
      <option value="">Select a Timeslot</option>
      {#if timeslotsGrouped}
        {#each Object.keys(timeslotsGrouped) as timeslot}
          <option value={timeslot}>{convert24HoursFormat(timeslot)}</option>
        {/each}
      {/if}
    </select>
  </div>

  <div class="grid grid-cols-3 gap-2 mt-4">
    {#if showTimeslotsChildrens}
      {#each timeslotsChildrens as timeslot}
        <button
          class={`px-4 py-4 border rounded-lg cursor-pointer ${selectedTimeslot === timeslot ? "bg-brand text-white" : "border-gray-300"}`}
          data-value={timeslot}
          on:click={() => {
            timeslotChange({ target: { value: timeslot } });
          }}
        >
          {convert24HoursFormat(timeslot)}
        </button>
      {/each}
    {/if}
  </div>

  <div class="mt-6 flex flex-row w-full">
    <div class="w-20">
      <button
        on:click={() => step.update((value) => value - 1)}
        class="text-xl focus:outline-none hover:text-brand transition ease-in-out bg-gray-200 text-gray-800 inline-block w-12 h-12 rounded-lg relative flex flex-col items-center justify-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 mx-auto"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
    </div>
    <div class="flex-1">
      <button
        on:click={onNextStep}
        class="w-full bg-black text-white px-4 py-2 rounded-lg text-2xl"
        >Next</button
      >
    </div>
  </div>
</div>

<style></style>
